import { FC, useState } from "react";
import { despesa, servidor, usuarios } from "../../types/openData";
import {
  Box,
  Button,
  Container,
  Link,
  Typography,
  TextField,
  Alert,
} from "@mui/material";
import "./openData.css";
import { json2xml } from "xml-js";
import { useParams } from "react-router-dom";
import * as despesasApi from "../../apis/transparency-cge-despesas";
import * as servidoresApi from "../../apis/transparency-cge-servidores";

export const OpenDataFormContainer: FC = () => {
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [endpointData, setEndpointData] = useState<any>(null);
  const [notification, setNotification] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  interface Endpoint {
    slug: string;
    description: string;
    url: string;
  }

  const { resources, category, year, query } = useParams<{
    resources: string;
    category: string;
    year: string;
    query: string;
  }>();

  const findEndpoint = () => {
    let endpointList: Endpoint[] = [];

    if (resources === "despesa") {
      endpointList = despesa;
    } else if (resources === "servidor") {
      endpointList = servidor;
    } else if (resources === "usuarios") {
      endpointList = usuarios;
    }

    let endpointPath = "";
    if (category && year && query) {
      endpointPath = `${resources}/${category}/${year}/${query}`;
    } else if (category && query) {
      endpointPath = `${resources}/${category}/${query}`;
    } else if (category) {
      endpointPath = `${resources}/${category}`;
    } else {
      endpointPath = `${resources}`;
    }

    return endpointList.find((ep) => ep.slug.startsWith(endpointPath));
  };

  const extractParamsFromUrl = (url: string): string[] => {
    const paramRegex = /{([^}]+)}/g;
    const matches = url.match(paramRegex) || [];
    return matches.map((match) => match.replace(/{|}/g, ""));
  };

  const fetchEndpointData = async () => {
    if (!endpoint) {
      setNotification({
        message: "Endpoint não encontrado",
        severity: "error",
      });
      return;
    }

    const requiredParams = extractParamsFromUrl(endpoint.url);
    const missingParams = requiredParams.filter(
      (param) => !params[param] || params[param].trim() === ""
    );

    if (missingParams.length > 0) {
      setError(`Missing required parameters: ${missingParams.join(", ")}`);
      setNotification({
        message: `Parâmetros obrigatórios: ${missingParams.join(", ")}`,
        severity: "error",
      });
      return;
    }

    setLoading(true);
    setError(null);
    setNotification(null);

    try {
      const endpointSlug = endpoint.slug;

      const isDownloadEndpoint = endpointSlug.includes("download");

      if (isDownloadEndpoint) {
        let response;
        let filename = `${resources}_${
          new Date().toISOString().split("T")[0]
        }.csv`;

        if (resources === "despesa" && endpointSlug === "despesa/download") {
          response = await despesasApi.getDespesasDownload(
            params.exercicio,
            params.referencia_ate,
            params.referencia_de
          );
        } else if (
          resources === "servidor" &&
          endpointSlug === "servidor/download"
        ) {
          response = await servidoresApi.getServidoresDownload(
            params.exercicio,
            params.referencia
          );
        } else if (resources === "usuarios") {
          if (endpointSlug.includes("download")) {
            downloadUserFile(endpoint.url);
          }
        }

        if (response) {
          const contentDisposition = response.headers["content-disposition"];
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, "");
            }
          }

          downloadFile(response.data, filename, "text/csv");
        }
      } else {
        let data;

        if (resources === "despesa") {
          if (endpointSlug === "despesa/detalhamento") {
            data = await despesasApi.getDespesasDetalhamento(
              params.exercicio,
              params.empenho_codigo,
              params.unidade_gestora_codigo
            );
          } else if (endpointSlug === "despesa/historico") {
            data = await despesasApi.getDespesasHistorico(
              params.exercicio,
              params.empenho_codigo,
              params.unidade_gestora_codigo
            );
          } else if (endpointSlug === "despesa/listagem") {
            data = await despesasApi.getDespesasListagem(
              params.exercicio,
              params.referencia_ate,
              params.referencia_de
            );
          } else if (endpointSlug === "despesa/exercicios") {
            data = await despesasApi.getDespesasExercicios();
          } else if (endpointSlug === "despesa/orgaos") {
            data = await despesasApi.getDespesasOrgaos(
              params.exercicio,
              params.referencia_ate,
              params.referencia_de
            );
          } else if (endpointSlug === "despesa/referencias") {
            data = await despesasApi.getDespesasReferencias(params.exercicio);
          } else if (endpointSlug === "despesa/lastUpdate") {
            data = await despesasApi.getDespesasLastUpdate();
          }
        } else if (resources === "servidor") {
          if (endpointSlug === "servidor/detalhamento") {
            data = await servidoresApi.getServidoresDetalhamento(
              params.exercicio,
              params.id
            );
          } else if (endpointSlug === "servidor/listagem") {
            data = await servidoresApi.getServidoresListagem(
              params.exercicio,
              params.referencia
            );
          } else if (endpointSlug === "servidor/exercicios") {
            data = await servidoresApi.getServidoresExercicios();
          } else if (endpointSlug === "servidor/orgaos") {
            data = await servidoresApi.getServidoresOrgaos(
              params.exercicio,
              params.referencia_ate,
              params.referencia_de
            );
          } else if (endpointSlug === "servidor/referencias") {
            data = await servidoresApi.getServidoresReferencias(
              params.exercicio
            );
          } else if (endpointSlug === "servidor/lastUpdate") {
            data = await servidoresApi.getServidoresLastUpdate();
          }
        }

        setEndpointData(data);
        setResponse({ data });
      }
    } catch (err: any) {
      setError(`Failed to fetch data: ${err.message || "Unknown error"}`);
      setNotification({
        message: `Não foi possível completar a ação: ${
          err.message || "Unknown error"
        }`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (param: string, value: string) => {
    setParams((prev) => ({ ...prev, [param]: value }));
  };

  function convertToCSV(arr: any) {
    if (!Array.isArray(arr) || arr.length === 0) {
      return "";
    }
    const array = [Object.keys(arr[0])].concat(arr);

    return array
      .map((it) => {
        return Object.values(it).toString();
      })
      .join("\n");
  }

  function downloadUserFile(url: string) {
    const a = document.createElement("a");
    const fullUrl = `${process.env.BUCKET_USER_DOWNLOAD_URL}${url}` || "";
    a.href = fullUrl;
    a.target = "_blank";
    a.click();
  }

  function downloadFile(content: any, fileName: string, contentType: string) {
    const a = document.createElement("a");
    const file = new Blob([content], { type: contentType });
    a.href = URL.createObjectURL(file);
    a.download = fileName;
    a.click();
  }

  function onDownload(format: "json" | "csv" | "xml") {
    const jsonFile = JSON.stringify(response?.data || {}, null, 2);
    if (format === "json") {
      downloadFile(jsonFile, "export.json", "application/json");
    } else if (format === "csv") {
      let csvFile = "";
      if (Array.isArray(response?.data)) {
        csvFile = convertToCSV(response.data);
      } else {
        csvFile = convertToCSV([response?.data || {}]);
      }
      downloadFile(csvFile, "export.csv", "text/csv;charset=UTF-8");
    } else if (format === "xml") {
      const xmlFile = json2xml(jsonFile, { compact: true, spaces: 1 });
      downloadFile(xmlFile, "export.xml", "application/xml");
    }
  }

  const baseUrl =
    resources === "despesa"
      ? despesasApi.apiUrlDespesas
      : resources === "servidor"
      ? servidoresApi.apiUrlServidores
      : resources === "usuarios"
      ? process.env.BUCKET_USER_DOWNLOAD_URL || ""
      : "";

  const endpoint = findEndpoint();
  const fullUrl = endpoint ? `${baseUrl}${endpoint.url}` : "";

  return (
    <Box>
      <Container maxWidth="lg" sx={{ padding: 2 }}>
        <Box className="MuiBox_Content">
          <Box sx={{ flexDirection: "column", width: "100%" }}>
            {endpoint && (
              <Box sx={{ mt: 2 }}>
                <Typography className="MuiTypography_Content">
                  {endpoint.description}
                </Typography>
                <Typography className="MuiTypography_URL">
                  URL:
                  <Typography
                    sx={{
                      ml: "0.5rem",
                      fontSize: "14px",
                      color: "#206b82",
                      lineBreak: "anywhere",
                    }}
                  >
                    {fullUrl}
                  </Typography>
                </Typography>
              </Box>
            )}

            {endpoint && (
              <div>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: "1rem",
                  }}
                >
                  {extractParamsFromUrl(endpoint.url).map((param) => (
                    <TextField
                      key={param}
                      id={`param-${param}`}
                      label={param}
                      value={params[param] || ""}
                      onChange={(e) => handleParamChange(param, e.target.value)}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    className="button_mui"
                    variant="contained"
                    onClick={fetchEndpointData}
                    disabled={loading}
                    sx={{ mt: 2, mb: 2 }}
                  >
                    {loading ? "Carregando..." : "Buscar Dados"}
                  </Button>
                </Box>
              </div>
            )}

            {endpointData && (
              <Box
                sx={{
                  display: "flex",
                  mb: "0.5rem",
                  height: "35px",
                  alignSelf: "end",
                  justifyContent: "flex-end",
                  width: "100%",
                  mt: 2,
                }}
              >
                <Button
                  className="button_mui"
                  onClick={() => onDownload("json")}
                >
                  JSON
                </Button>
                <Button
                  className="button_mui"
                  onClick={() => onDownload("csv")}
                  sx={{ ml: "0.5rem" }}
                >
                  CSV
                </Button>
                <Button
                  className="button_mui"
                  onClick={() => onDownload("xml")}
                  sx={{ ml: "0.5rem" }}
                >
                  XML
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {endpointData && (
          <pre
            style={{
              height: "100%",
              maxHeight: 500,
              margin: "0 auto",
            }}
          >
            <code id="code-view">{JSON.stringify(response, null, 2)}</code>
          </pre>
        )}

        {notification && (
          <Alert
            severity={notification.severity}
            className="MuiAlert_openData"
            sx={{ mt: 2 }}
          >
            {notification.message}
          </Alert>
        )}
      </Container>
    </Box>
  );
};
